import React, { useState, useEffect } from 'react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    advertising: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Delay showing banner slightly for better UX
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      const saved = JSON.parse(consent);
      setPreferences(saved);
      loadScripts(saved);
    }
  }, []);

  const loadScripts = (prefs) => {
    // Load Google Analytics if analytics consent is given
    if (prefs.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }

    // Load Google Ads if advertising consent is given
    if (prefs.advertising && window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted'
      });
    }
  };

  const handleAcceptAll = () => {
    const allPrefs = {
      necessary: true,
      analytics: true,
      advertising: true,
    };
    setPreferences(allPrefs);
    localStorage.setItem('cookieConsent', JSON.stringify(allPrefs));
    loadScripts(allPrefs);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const minimalPrefs = {
      necessary: true,
      analytics: false,
      advertising: false,
    };
    setPreferences(minimalPrefs);
    localStorage.setItem('cookieConsent', JSON.stringify(minimalPrefs));
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    loadScripts(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(10, 10, 10, 0.98)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid #333',
        padding: '1.5rem',
        zIndex: 9999,
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: window.innerWidth < 768 ? 'column' : 'row',
          alignItems: window.innerWidth < 768 ? 'stretch' : 'center',
          gap: '1.5rem',
        }}>
          {/* Text Content */}
          <div style={{ flex: 1 }}>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '700',
              color: '#fff',
              margin: '0 0 0.5rem 0',
            }}>
              🍪 We value your privacy
            </h3>
            <p style={{
              fontSize: '0.9rem',
              color: '#ccc',
              margin: 0,
              lineHeight: '1.5',
            }}>
              We use cookies to enhance your browsing experience, serve personalized ads, and analyze our traffic. 
              By clicking "Accept All", you consent to our use of cookies for analytics and advertising.
            </p>
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap',
            flexShrink: 0,
          }}>
            <button
              onClick={() => setShowSettings(true)}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'transparent',
                border: '2px solid #fff',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#fff';
                e.target.style.color = '#000';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#fff';
              }}
            >
              Cookie Settings
            </button>
            <button
              onClick={handleRejectAll}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#333',
                border: '2px solid #333',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#444';
                e.target.style.borderColor = '#444';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#333';
                e.target.style.borderColor = '#333';
              }}
            >
              Reject All
            </button>
            <button
              onClick={handleAcceptAll}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#1DB954',
                border: '2px solid #1DB954',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#1ed760';
                e.target.style.borderColor = '#1ed760';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#1DB954';
                e.target.style.borderColor = '#1DB954';
              }}
            >
              Accept All
            </button>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          zIndex: 10000,
        }}>
          <div style={{
            background: '#0a0a0a',
            border: '1px solid #333',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '2rem',
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#fff',
              margin: '0 0 1.5rem 0',
            }}>
              Cookie Preferences
            </h2>

            {/* Necessary Cookies */}
            <div style={{
              padding: '1.5rem',
              background: '#111',
              borderRadius: '8px',
              marginBottom: '1rem',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
              }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#fff',
                  margin: 0,
                }}>
                  Necessary Cookies
                </h3>
                <span style={{
                  padding: '0.25rem 0.75rem',
                  background: '#333',
                  color: '#999',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                }}>
                  Always Active
                </span>
              </div>
              <p style={{
                fontSize: '0.85rem',
                color: '#999',
                margin: 0,
                lineHeight: '1.5',
              }}>
                Essential for the website to function. Cannot be disabled.
              </p>
            </div>

            {/* Analytics Cookies */}
            <div style={{
              padding: '1.5rem',
              background: '#111',
              borderRadius: '8px',
              marginBottom: '1rem',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
              }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#fff',
                  margin: 0,
                }}>
                  Analytics Cookies
                </h3>
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '50px',
                  height: '26px',
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    style={{ display: 'none' }}
                  />
                  <span style={{
                    position: 'absolute',
                    inset: 0,
                    background: preferences.analytics ? '#1DB954' : '#333',
                    borderRadius: '13px',
                    transition: 'background 0.2s',
                  }}>
                    <span style={{
                      position: 'absolute',
                      height: '20px',
                      width: '20px',
                      left: preferences.analytics ? '27px' : '3px',
                      bottom: '3px',
                      background: '#fff',
                      borderRadius: '50%',
                      transition: 'left 0.2s',
                    }} />
                  </span>
                </label>
              </div>
              <p style={{
                fontSize: '0.85rem',
                color: '#999',
                margin: 0,
                lineHeight: '1.5',
              }}>
                Help us understand how visitors interact with our website (Google Analytics).
              </p>
            </div>

            {/* Advertising Cookies */}
            <div style={{
              padding: '1.5rem',
              background: '#111',
              borderRadius: '8px',
              marginBottom: '1.5rem',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
              }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#fff',
                  margin: 0,
                }}>
                  Advertising Cookies
                </h3>
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '50px',
                  height: '26px',
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={preferences.advertising}
                    onChange={(e) => setPreferences({ ...preferences, advertising: e.target.checked })}
                    style={{ display: 'none' }}
                  />
                  <span style={{
                    position: 'absolute',
                    inset: 0,
                    background: preferences.advertising ? '#1DB954' : '#333',
                    borderRadius: '13px',
                    transition: 'background 0.2s',
                  }}>
                    <span style={{
                      position: 'absolute',
                      height: '20px',
                      width: '20px',
                      left: preferences.advertising ? '27px' : '3px',
                      bottom: '3px',
                      background: '#fff',
                      borderRadius: '50%',
                      transition: 'left 0.2s',
                    }} />
                  </span>
                </label>
              </div>
              <p style={{
                fontSize: '0.85rem',
                color: '#999',
                margin: 0,
                lineHeight: '1.5',
              }}>
                Used to deliver personalized ads and measure ad campaign effectiveness (Google Ads).
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => setShowSettings(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'transparent',
                  border: '2px solid #fff',
                  color: '#fff',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#fff';
                  e.target.style.color = '#000';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#fff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSettings}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#1DB954',
                  border: '2px solid #1DB954',
                  color: '#fff',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#1ed760';
                  e.target.style.borderColor = '#1ed760';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#1DB954';
                  e.target.style.borderColor = '#1DB954';
                }}
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;